// Chemin: C:\gestion-copro\src\services\coproprietaire.service.ts
// Session 47 : Source unique lot.proprietaireId pour les lots possédés

import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import {
  Coproprietaire,
  CoproprietaireCreateData,
  CoproprietaireUpdateData,
  CoproprietaireAvecLots
} from '../types/coproprietaire.types';
import { Lot } from '../types/lot.types';

// Collection racine (même pattern que lots, interlocuteurs, etc.)
const COLLECTION_NAME = 'coproprietaires';
const LOTS_COLLECTION = 'lots';

/**
 * Récupère tous les copropriétaires d'une copropriété
 */
export const getCoproprietaires = async (coproprieteId: string): Promise<Coproprietaire[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    orderBy('nom', 'asc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Coproprietaire));
};

/**
 * Récupère un copropriétaire par son ID
 */
export const getCoproprietaireById = async (
  coproprieteId: string,
  coproprietaireId: string
): Promise<Coproprietaire | null> => {
  const docRef = doc(db, COLLECTION_NAME, coproprietaireId);
  const snapshot = await getDoc(docRef);
  
  if (!snapshot.exists()) {
    return null;
  }
  
  const data = snapshot.data();
  // Vérifier que le copropriétaire appartient bien à la copropriété
  if (data.coproprieteId !== coproprieteId) {
    return null;
  }
  
  return {
    id: snapshot.id,
    ...data
  } as Coproprietaire;
};

/**
 * Récupère un copropriétaire par son userId (compte Firebase Auth)
 */
export const getCoproprietaireByUserId = async (
  coproprieteId: string,
  userId: string
): Promise<Coproprietaire | null> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('userId', '==', userId)
  );
  
  const snapshot = await getDocs(q);
  
  if (snapshot.empty) {
    return null;
  }
  
  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as Coproprietaire;
};

/**
 * Crée un nouveau copropriétaire
 */
export const createCoproprietaire = async (
  data: CoproprietaireCreateData
): Promise<string> => {
  const now = Timestamp.now();
  
  const coproprietaireData = {
    ...data,
    lotIds: data.lotIds || [],
    statutCompte: 'non_invite' as const,
    createdAt: now,
    updatedAt: now
  };
  
  const docRef = await addDoc(collection(db, COLLECTION_NAME), coproprietaireData);
  
  // Si des lots sont liés, mettre à jour leur proprietaireId
  if (data.lotIds && data.lotIds.length > 0) {
    await updateLotsProprietaire(data.lotIds, docRef.id);
  }
  
  return docRef.id;
};

/**
 * Met à jour un copropriétaire
 */
export const updateCoproprietaire = async (
  coproprieteId: string,
  coproprietaireId: string,
  data: CoproprietaireUpdateData
): Promise<void> => {
  const docRef = doc(db, COLLECTION_NAME, coproprietaireId);
  
  // Récupérer l'ancien copropriétaire pour gérer les changements de lots
  const oldCoproprietaire = await getCoproprietaireById(coproprieteId, coproprietaireId);
  
  // Session 46 : Nettoyer les champs undefined (Firestore les rejette)
  const cleanData: Record<string, any> = { updatedAt: Timestamp.now() };
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      cleanData[key] = value;
    }
  }
  
  await updateDoc(docRef, cleanData);
  
  // Gérer les changements de lots si lotIds a changé
  if (data.lotIds !== undefined && oldCoproprietaire) {
    const oldLotIds = oldCoproprietaire.lotIds || [];
    const newLotIds = data.lotIds || [];
    
    // Lots retirés : supprimer le proprietaireId
    const lotsRetires = oldLotIds.filter(id => !newLotIds.includes(id));
    if (lotsRetires.length > 0) {
      await updateLotsProprietaire(lotsRetires, null);
    }
    
    // Lots ajoutés : définir le proprietaireId
    const lotsAjoutes = newLotIds.filter(id => !oldLotIds.includes(id));
    if (lotsAjoutes.length > 0) {
      await updateLotsProprietaire(lotsAjoutes, coproprietaireId);
    }
  }
};

/**
 * Supprime un copropriétaire
 */
export const deleteCoproprietaire = async (
  coproprieteId: string,
  coproprietaireId: string
): Promise<void> => {
  // Session 47 : Récupérer les lots via proprietaireId (source unique)
  const lotsQuery = query(
    collection(db, LOTS_COLLECTION),
    where('coproprieteId', '==', coproprieteId),
    where('proprietaireId', '==', coproprietaireId)
  );
  const lotsSnapshot = await getDocs(lotsQuery);
  
  // Retirer le proprietaireId des lots liés
  if (!lotsSnapshot.empty) {
    const lotIds = lotsSnapshot.docs.map(doc => doc.id);
    await updateLotsProprietaire(lotIds, null);
  }
  
  const docRef = doc(db, COLLECTION_NAME, coproprietaireId);
  await deleteDoc(docRef);
};

/**
 * Met à jour le proprietaireId de plusieurs lots
 * @param proprietaireId - ID du copropriétaire ou null pour délier
 */
const updateLotsProprietaire = async (
  lotIds: string[],
  proprietaireId: string | null
): Promise<void> => {
  if (lotIds.length === 0) return;
  
  const batch = writeBatch(db);
  
  for (const lotId of lotIds) {
    const lotRef = doc(db, LOTS_COLLECTION, lotId);
    batch.update(lotRef, { 
      proprietaireId: proprietaireId,
      updatedAt: Timestamp.now()
    });
  }
  
  await batch.commit();
};

/**
 * Récupère un copropriétaire avec ses lots enrichis
 * Session 47 : Utilise lot.proprietaireId comme source unique (plus de désync)
 * Session 72 : Inclut les lots rattachés (parking, cave, etc.) dans le calcul
 */
export const getCoproprietaireAvecLots = async (
  coproprieteId: string,
  coproprietaireId: string
): Promise<CoproprietaireAvecLots | null> => {
  const coproprietaire = await getCoproprietaireById(coproprieteId, coproprietaireId);
  
  if (!coproprietaire) {
    return null;
  }
  
  // Session 47 : Récupérer les lots via proprietaireId (SOURCE UNIQUE)
  const lotsQuery = query(
    collection(db, LOTS_COLLECTION),
    where('coproprieteId', '==', coproprieteId),
    where('proprietaireId', '==', coproprietaireId)
  );
  const lotsSnapshot = await getDocs(lotsQuery);
  
  const lots: CoproprietaireAvecLots['lots'] = [];
  let tantiemesTotal = 0;
  const lotsPrincipauxIds: string[] = [];
  
  // Ajouter les lots principaux
  lotsSnapshot.docs.forEach(doc => {
    const lotData = doc.data() as Lot;
    const tantiemes = lotData.tantièmesGeneraux || 0;
    lots.push({
      id: doc.id,
      numero: lotData.numero,
      type: lotData.type,
      tantiemes
    });
    tantiemesTotal += tantiemes;
    lotsPrincipauxIds.push(doc.id);
  });
  
  // Session 72 : Récupérer les lots rattachés à ces lots principaux
  if (lotsPrincipauxIds.length > 0) {
    const allLotsQuery = query(
      collection(db, LOTS_COLLECTION),
      where('coproprieteId', '==', coproprieteId)
    );
    const allLotsSnapshot = await getDocs(allLotsQuery);
    
    allLotsSnapshot.docs.forEach(doc => {
      const lotData = doc.data() as Lot;
      // Si ce lot est rattaché à l'un des lots principaux du copropriétaire
      if (lotData.lotPrincipalId && lotsPrincipauxIds.includes(lotData.lotPrincipalId)) {
        const tantiemes = lotData.tantièmesGeneraux || 0;
        lots.push({
          id: doc.id,
          numero: lotData.numero,
          type: lotData.type,
          tantiemes
        });
        tantiemesTotal += tantiemes;
      }
    });
  }
  
  // Trier par numéro de lot
  lots.sort((a, b) => a.numero.localeCompare(b.numero, 'fr', { numeric: true }));
  
  return {
    ...coproprietaire,
    lots,
    tantiemesTotal
  };
};

/**
 * Récupère tous les copropriétaires avec leurs tantièmes calculés
 * Session 47 : Utilise lot.proprietaireId comme source unique (plus de désync)
 */
export const getCoproprietairesAvecTantiemes = async (
  coproprieteId: string
): Promise<(Coproprietaire & { tantiemesTotal: number; lotsDetails: string[] })[]> => {
  const coproprietaires = await getCoproprietaires(coproprieteId);
  
  // Récupérer tous les lots de la copropriété une seule fois
  const lotsQuery = query(
    collection(db, LOTS_COLLECTION),
    where('coproprieteId', '==', coproprieteId)
  );
  const lotsSnapshot = await getDocs(lotsQuery);
  
  // Session 47 : Grouper les lots par proprietaireId (SOURCE UNIQUE)
  // au lieu d'utiliser coproprietaire.lotIds qui peut être désynchronisé
  const lotsByProprietaire = new Map<string, Lot[]>();
  lotsSnapshot.docs.forEach(doc => {
    const lot = { id: doc.id, ...doc.data() } as Lot;
    if (lot.proprietaireId) {
      const existing = lotsByProprietaire.get(lot.proprietaireId) || [];
      existing.push(lot);
      lotsByProprietaire.set(lot.proprietaireId, existing);
    }
  });
  
  // Enrichir chaque copropriétaire avec ses lots (via proprietaireId)
  return coproprietaires.map(copro => {
    const lotsOfCopro = lotsByProprietaire.get(copro.id) || [];
    
    let tantiemesTotal = 0;
    const lotsDetails: string[] = [];
    
    // Trier par numéro de lot
    lotsOfCopro.sort((a, b) => a.numero.localeCompare(b.numero, 'fr', { numeric: true }));
    
    for (const lot of lotsOfCopro) {
      tantiemesTotal += lot.tantièmesGeneraux || 0;
      lotsDetails.push(lot.numero);
    }
    
    return {
      ...copro,
      tantiemesTotal,
      lotsDetails
    };
  });
};

/**
 * Récupère les copropriétaires par statut de compte
 */
export const getCoproprietairesByStatut = async (
  coproprieteId: string,
  statut: Coproprietaire['statutCompte']
): Promise<Coproprietaire[]> => {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('coproprieteId', '==', coproprieteId),
    where('statutCompte', '==', statut),
    orderBy('nom', 'asc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Coproprietaire));
};

/**
 * Marque un copropriétaire comme invité
 */
export const marquerCommeInvite = async (
  coproprieteId: string,
  coproprietaireId: string
): Promise<void> => {
  await updateCoproprietaire(coproprieteId, coproprietaireId, {
    statutCompte: 'invite',
    dateInvitation: Timestamp.now()
  });
};

/**
 * Active le compte d'un copropriétaire (après création du compte Auth)
 */
export const activerCompte = async (
  coproprieteId: string,
  coproprietaireId: string,
  userId: string
): Promise<void> => {
  await updateCoproprietaire(coproprieteId, coproprietaireId, {
    statutCompte: 'actif',
    userId,
    dateActivation: Timestamp.now()
  });
};

/**
 * Récupère les lots non attribués (sans proprietaireId)
 */
export const getLotsNonAttribues = async (
  coproprieteId: string
): Promise<Lot[]> => {
  // On récupère tous les lots et on filtre côté client
  // car Firestore ne supporte pas bien les requêtes sur champs null/undefined
  const q = query(
    collection(db, LOTS_COLLECTION),
    where('coproprieteId', '==', coproprieteId)
  );
  const snapshot = await getDocs(q);
  
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() } as Lot))
    .filter(lot => !lot.proprietaireId);
};

/**
 * Statistiques des copropriétaires
 */
export const getStatistiquesCoproprietaires = async (
  coproprieteId: string
): Promise<{
  total: number;
  avecCompteActif: number;
  invites: number;
  nonInvites: number;
  avecEmail: number;
  sansEmail: number;
}> => {
  const coproprietaires = await getCoproprietaires(coproprieteId);
  
  return {
    total: coproprietaires.length,
    avecCompteActif: coproprietaires.filter(c => c.statutCompte === 'actif').length,
    invites: coproprietaires.filter(c => c.statutCompte === 'invite').length,
    nonInvites: coproprietaires.filter(c => c.statutCompte === 'non_invite').length,
    avecEmail: coproprietaires.filter(c => c.email).length,
    sansEmail: coproprietaires.filter(c => !c.email).length
  };
};

// ============================================
// IMPORT CSV
// ============================================

/**
 * Structure des données parsées depuis le CSV
 */
export interface CoproprietaireCSVRow {
  nom: string;
  prenom: string;
  email?: string;
  telephone?: string;
  lots?: string; // Numéros de lots séparés par des virgules
}

/**
 * Résultat de l'import
 */
export interface ImportResult {
  total: number;
  crees: number;
  erreurs: { ligne: number; message: string }[];
  lotsTrouves: number;
  lotsNonTrouves: string[];
}

/**
 * Importe des copropriétaires en masse depuis des données CSV parsées
 * et lie automatiquement les lots existants
 */
export const importCoproprietairesEnMasse = async (
  coproprieteId: string,
  rows: CoproprietaireCSVRow[]
): Promise<ImportResult> => {
  const result: ImportResult = {
    total: rows.length,
    crees: 0,
    erreurs: [],
    lotsTrouves: 0,
    lotsNonTrouves: []
  };

  // Récupérer tous les lots de la copropriété pour la liaison
  const lotsQuery = query(
    collection(db, LOTS_COLLECTION),
    where('coproprieteId', '==', coproprieteId)
  );
  const lotsSnapshot = await getDocs(lotsQuery);
  const lotsMap = new Map<string, string>(); // numero -> id
  lotsSnapshot.docs.forEach(doc => {
    const data = doc.data();
    lotsMap.set(data.numero, doc.id);
  });

  // Traiter chaque ligne
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const ligneNum = i + 2; // +2 car ligne 1 = headers, et index commence à 0

    // Validation
    if (!row.nom || !row.prenom) {
      result.erreurs.push({
        ligne: ligneNum,
        message: `Nom ou prénom manquant`
      });
      continue;
    }

    try {
      // Trouver les IDs des lots
      const lotIds: string[] = [];
      if (row.lots) {
        const numerosLots = row.lots.split(',').map(n => n.trim()).filter(n => n);
        for (const numero of numerosLots) {
          const lotId = lotsMap.get(numero);
          if (lotId) {
            lotIds.push(lotId);
            result.lotsTrouves++;
          } else {
            if (!result.lotsNonTrouves.includes(numero)) {
              result.lotsNonTrouves.push(numero);
            }
          }
        }
      }

      // Créer le copropriétaire
      const createData: CoproprietaireCreateData = {
        coproprieteId,
        nom: row.nom.trim(),
        prenom: row.prenom.trim(),
        lotIds
      };

      if (row.email && row.email.trim()) {
        createData.email = row.email.trim();
      }
      if (row.telephone && row.telephone.trim()) {
        createData.telephone = row.telephone.trim();
      }

      await createCoproprietaire(createData);
      result.crees++;

    } catch (error) {
      result.erreurs.push({
        ligne: ligneNum,
        message: error instanceof Error ? error.message : 'Erreur inconnue'
      });
    }
  }

  return result;
};

/**
 * Supprime tous les copropriétaires d'une copropriété
 * (utile pour réimporter proprement)
 */
export const deleteAllCoproprietaires = async (
  coproprieteId: string
): Promise<number> => {
  const coproprietaires = await getCoproprietaires(coproprieteId);
  
  for (const copro of coproprietaires) {
    await deleteCoproprietaire(coproprieteId, copro.id);
  }
  
  return coproprietaires.length;
};
